document.documentElement.classList.remove('no-js');

class MobileMenu extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    const nav = document.getElementById(button?.getAttribute('aria-controls'));
    if (!button || !nav) return;
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      nav.classList.toggle('is-open', !open);
    });
  }
}
customElements.define('mobile-menu', MobileMenu);

class QuantityInput extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input');
    this.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
      const change = button.dataset.action === 'increase' ? 1 : -1;
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + change);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }));
  }
}
customElements.define('quantity-input', QuantityInput);

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input[type="search"]');
    const results = this.querySelector('[data-results]');
    let timer;
    const close = () => { results.innerHTML = ''; };
    input?.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) { close(); return; }
      timer = setTimeout(() => fetch(`${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search`).then((response) => response.text()).then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        results.innerHTML = html?.innerHTML || '';
      }).catch(close), 180);
    });
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        input.blur();
      }
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) close();
    });
  }
}
customElements.define('predictive-search', PredictiveSearch);

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.data = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '[]');
    this.form?.addEventListener('change', () => this.updateVariant());
  }
  updateVariant() {
    const values = [...this.form.querySelectorAll('[data-option]:checked, select[data-option]')].map((field) => field.value);
    const variant = this.data.find((item) => item.options.every((value, index) => value === values[index]));
    const id = this.form.querySelector('[name="id"]');
    const button = this.form.querySelector('[name="add"]');
    if (!variant) { button.disabled = true; button.textContent = 'Unavailable'; return; }
    id.value = variant.id;
    button.disabled = !variant.available;
    button.textContent = variant.available ? 'Add to cart' : 'Sold out';
    const price = document.querySelector('[data-product-price]');
    if (price) price.textContent = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(variant.price / 100);
    history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
  }
}
customElements.define('product-form', ProductForm);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('video[autoplay]').forEach((video) => video.play().catch(() => {}));
});

// Contact and quote links work even before dedicated Shopify pages are created.
function openStoreDialog(hash) {
  const id = ['#contact', '#ContactForm-dialog'].includes(hash) ? 'ContactDialog' : ['#custom-quote', '#QuoteForm-dialog'].includes(hash) ? 'QuoteDialog' : null;
  const dialog = id && document.getElementById(id);
  if (!dialog) return false;
  if (!dialog.open) dialog.showModal();
  return true;
}
document.addEventListener('click', (event) => {
  const close = event.target.closest('[data-dialog-close]');
  if (close) close.closest('dialog').close();
  const link = event.target.closest('a[href]');
  if (link) {
    const url = new URL(link.href, location.href);
    if (url.origin === location.origin && openStoreDialog(url.hash)) event.preventDefault();
  }
});
document.querySelectorAll('.store-dialog').forEach((dialog) => {
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
  });
});
openStoreDialog(location.hash);
document.querySelectorAll('.store-dialog').forEach((dialog) => {
  if (dialog.querySelector('.form-success, .errors') && !dialog.open) dialog.showModal();
});
window.addEventListener('hashchange', () => openStoreDialog(location.hash));

function loadPrivacyApi() {
  if (window.Shopify?.customerPrivacy) return Promise.resolve(window.Shopify.customerPrivacy);
  return new Promise((resolve, reject) => {
    if (!window.Shopify?.loadFeatures) return reject(new Error('Privacy settings could not load. Please try again.'));
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error || !window.Shopify.customerPrivacy) return reject(new Error('Privacy settings could not load. Please try again.'));
      resolve(window.Shopify.customerPrivacy);
    });
  });
}
document.addEventListener('click', async (event) => {
  if (!event.target.closest('[data-privacy-choices]')) return;
  const dialog = document.getElementById('PrivacyDialog');
  const status = dialog.querySelector('[data-privacy-status]');
  dialog.showModal();
  status.textContent = 'Loading your choices…';
  const button = dialog.querySelector('[type="submit"]');
  button.disabled = true;
  try {
    const api = await loadPrivacyApi();
    const consent = api.currentVisitorConsent();
    dialog.querySelectorAll('[type="checkbox"]').forEach((input) => {
      input.checked = consent[input.name] === 'yes';
    });
    status.textContent = '';
    button.disabled = false;
  } catch (error) { status.textContent = error.message; }
});
document.querySelector('[data-privacy-form]')?.addEventListener('submit', async (event) => {
  event.preventDefault();
  const form = event.target;
  const status = form.querySelector('[data-privacy-status]');
  const button = form.querySelector('[type="submit"]');
  button.disabled = true;
  try {
    const api = await loadPrivacyApi();
    const consent = Object.fromEntries([...form.querySelectorAll('[type="checkbox"]')].map((input) => [input.name, input.checked]));
    api.setTrackingConsent(consent, () => {
      status.textContent = 'Your privacy choices have been saved.';
      button.disabled = false;
    });
  } catch (error) { status.textContent = error.message; button.disabled = false; }
});
